import { createReducer } from '@reduxjs/toolkit';
import { ClientListLienholdersResult, ResultClientList } from '@rapid-recovery-agency-inc/insightt-shared';
import {
  fetchLenderDetails,
  fetchClientListWithLienholders,
  fetchDailyViewStatsAction,
  fetchMtdAssignedAction,
  fetchMtdRepossessedAction,
  // fetchMtdYtdCompanyUncategorized,
  fetchPcymCompanyStatsAction,
  fetchRHLClientList,
  // fetchTopAgentsAction,
  fetchYtdCompanyStatsAction,
  fetchLienholderDetails,
  fetchLienholderWithLenderDetails,
  upsertTRRLender,
  openEditClientGoalModal,
  closeEditClientGoalModal,
  openEditClientGoalModalLienholder,
  closeEditClientGoalModalLienholder,
  upsertTRRLienholder,
  fetchNewTopAgentsAction,
  fetchNewTopAgentsViewAllAction,
  setTopAgentsViewAll,
  setSpottersFirstLoading,
} from './reports-actions';
import { REPORTS_INITIAL_STATE } from './reports-constants';

export type StatsByYear = {
  assignments: number[];
  missedRepossessions: number[];
  recoveryRates: number[];
  repossessions: number[];
};

export type PcymCompanyStats = {
  [key: number]: {
    [key2: number]: StatsByYear;
  };
};

export type YtdCompanyStats = {
  [key: number]: {
    [key2: number]: StatsByYear;
  };
};

export type KeyOfStatsByYear = keyof StatsByYear;

export type ViewStatsData = {
  loading: boolean;
  value: number;
  previousValue: number;
  percentage: number;
};

export type DailyViewStats = {
  accountNotChecked?: ViewStatsData;
  checkIns?: ViewStatsData;
  liveHits?: ViewStatsData;
  liveHitsNotSecured?: ViewStatsData;
  newAssignments?: ViewStatsData;
  recoverRates?: {
    value: number;
    repoValue: number;
    percentage: number;
    branchName: string;
  }[];
  repossessionsHitList?: ViewStatsData;
  scans?: ViewStatsData;
  spotted?: ViewStatsData;
  spottedNotSecured?: ViewStatsData;
  spottedVehicles?: ViewStatsData;
  totalRepossessions?: ViewStatsData;
  dailyRecoveryRate?: ViewStatsData;
};

interface ReportsReducer {
  rhlClientList: ResultClientList[];
  rhlClientListLoading: boolean;
  pcymCompanyStats: PcymCompanyStats;
  pcymCompanyStatsLoading: boolean;
  pcymCompanyStatsFirstLoading: boolean;
  ytdCompanyStats: YtdCompanyStats;
  ytdCompanyStatsLoading: boolean;
  ytdCompanyStatsFirstLoading: boolean;
  // topAgents: TopRecoveryAgentOutput;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  topAgents: any;
  topAgentsLoading: boolean;
  topAgentsFirstLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  topSpotters: any;
  topSpottersLoading: boolean;
  topSpottersFirstLoading: boolean;
  dailyViewStats: DailyViewStats;
  dailyViewStatsLoading: boolean;
  dailyViewStatsFirstLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mtdCurrentRepossessed: any;
  mtdCurrentRepossessedLoading: boolean;
  mtdCurrentRepossessedFirstLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mtdPreviousRepossessed: any;
  mtdPreviousRepossessedLoading: boolean;
  mtdPreviousRepossessedFirstLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mtdCurrentAssigned: any;
  mtdCurrentAssignedLoading: boolean;
  mtdCurrentAssignedFirstLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mtdPreviousAssigned: any;
  mtdPreviousAssignedLoading: boolean;
  mtdPreviousAssignedFirstLoading: boolean;
  // mtdytdCompanyUncategorized: MtdYtdCompanyUncategorized;
  // mtdytdCompanyUncategorizedLoading: boolean;
  // mtdytdCompanyUncategorizedFirstLoading: boolean;
  clientListData: ClientListLienholdersResult[];
  isClientListLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lenderDetailsData: any;
  lenderDetailsLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lienholderDetailsData: any;
  lienholderDetailsLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lienholderLenderDetailsData: any;
  lienholderLenderDetailsLoading: boolean;
  targetRecoveryRateLoading: boolean;
  isLenderEditTRRModalOpen: boolean;
  isLienholderEditTRRModalOpen: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  topAgentsViewAll: any;
  topAgentsViewAllLoading: boolean;
}

export default createReducer<ReportsReducer>(REPORTS_INITIAL_STATE, (builder) => {
  builder
    .addCase(fetchRHLClientList.fulfilled, (state, action) => {
      return {
        ...state,
        rhlClientListLoading: false,
        rhlClientList: action.payload.rhlClientList,
      };
    })
    // -- create spotted note --
    .addCase(fetchRHLClientList.pending, (state) => {
      return { ...state, rhlClientListLoading: true };
    })

    // PCYM Company Stats
    .addCase(fetchPcymCompanyStatsAction.fulfilled, (state, action) => {
      return {
        ...state,
        pcymCompanyStatsFirstLoading: false,
        pcymCompanyStatsLoading: false,
        pcymCompanyStats: action.payload.pcymCompanyStats,
      };
    })
    .addCase(fetchPcymCompanyStatsAction.pending, (state) => {
      return { ...state, pcymCompanyStatsLoading: true };
    })

    // YTD Company Stats
    .addCase(fetchYtdCompanyStatsAction.fulfilled, (state, action) => {
      return {
        ...state,
        ytdCompanyStatsFirstLoading: false,
        ytdCompanyStatsLoading: false,
        ytdCompanyStats: action.payload.ytdCompanyStats,
      };
    })
    .addCase(fetchYtdCompanyStatsAction.pending, (state) => {
      return { ...state, ytdCompanyStatsLoading: true };
    })
    // Top Agents
    .addCase(fetchNewTopAgentsAction.pending, (state) => {
      return { ...state, topAgentsLoading: true };
    })
    .addCase(fetchNewTopAgentsAction.rejected, (state) => {
      return { ...state, topAgentsLoading: false };
    })
    .addCase(fetchNewTopAgentsAction.fulfilled, (state, action) => {
      return {
        ...state,
        topAgentsFirstLoading: false,
        topAgentsLoading: false,
        topAgents: action.payload,
      };
    })
    // Top Agents View All
    .addCase(fetchNewTopAgentsViewAllAction.pending, (state) => {
      return { ...state, topAgentsViewAllLoading: true };
    })
    .addCase(fetchNewTopAgentsViewAllAction.rejected, (state) => {
      return { ...state, topAgentsViewAllLoading: false };
    })
    .addCase(fetchNewTopAgentsViewAllAction.fulfilled, (state, action) => {
      return {
        ...state,
        topAgentsViewAllLoading: false,
        topAgentsViewAll: action.payload,
      };
    })
    .addCase(setTopAgentsViewAll, (state, action) => {
      return {
        ...state,
        topAgentsViewAll: action.payload,
      };
    })
    // Top Spotters
    .addCase(setSpottersFirstLoading, (state, action) => {
      return { ...state, topSpottersFirstLoading: action.payload };
    })

    // Daily View Stats
    .addCase(fetchDailyViewStatsAction.fulfilled, (state, action) => {
      return {
        ...state,
        dailyViewStatsFirstLoading: false,
        dailyViewStatsLoading: false,
        dailyViewStats: action.payload.dailyViewStats,
      };
    })
    .addCase(fetchDailyViewStatsAction.pending, (state) => {
      return { ...state, dailyViewStatsLoading: true };
    })

    // MTD Repossessed
    .addCase(fetchMtdRepossessedAction.fulfilled, (state, action) => {
      const { mode, mtdRepossessed } = action.payload;
      const newState =
        mode === 'now'
          ? {
              mtdCurrentRepossessedFirstLoading: false,
              mtdCurrentRepossessedLoading: false,
              mtdCurrentRepossessed: mtdRepossessed,
            }
          : {
              mtdPreviousRepossessedFirstLoading: false,
              mtdPreviousRepossessedLoading: false,
              mtdPreviousRepossessed: mtdRepossessed,
            };

      return {
        ...state,
        ...newState,
      };
    })
    .addCase(fetchMtdRepossessedAction.pending, (state) => {
      return {
        ...state,
        mtdCurrentRepossessedLoading: true,
        mtdPreviousRepossessedLoading: true,
      };
    })

    // MTD Assigned
    .addCase(fetchMtdAssignedAction.fulfilled, (state, action) => {
      const { current, previous } = action.payload;

      return {
        ...state,
        mtdCurrentAssignedFirstLoading: false,
        mtdPreviousAssignedFirstLoading: false,
        mtdCurrentAssignedLoading: false,
        mtdPreviousAssignedLoading: false,
        mtdCurrentAssigned: current,
        mtdPreviousAssigned: previous,
      };
    })
    .addCase(fetchMtdAssignedAction.pending, (state) => {
      return {
        ...state,
        mtdCurrentAssignedLoading: true,
        mtdPreviousAssignedLoading: true,
      };
    })

    // MTD YTD Company Uncategorized
    // .addCase(fetchMtdYtdCompanyUncategorized.fulfilled, (state, action) => {
    //   const response = action.payload.mtdytdCompanyUncategorized;

    //   const mtdUncategorized = {
    //     assigned: response.MTDAssignments,
    //     respossessed: response.MTDRepossessions,
    //   };
    //   const ytdUncategorized = {
    //     assigned: response.YTDAssignments,
    //     respossessed: response.YTDRepossessions,
    //   };

    //   return {
    //     ...state,
    //     mtdytdCompanyUncategorizedFirstLoading: false,
    //     mtdytdCompanyUncategorizedLoading: false,
    //     mtdytdCompanyUncategorized: {
    //       mtd: mtdUncategorized,
    //       ytd: ytdUncategorized,
    //     },
    //   };
    // })
    // .addCase(fetchMtdYtdCompanyUncategorized.pending, (state) => {
    //   return { ...state, mtdytdCompanyUncategorizedLoading: true };
    // })

    // Client List Lienholders
    .addCase(fetchClientListWithLienholders.pending, (state) => {
      state.isClientListLoading = true;
    })
    .addCase(fetchClientListWithLienholders.rejected, (state) => {
      state.isClientListLoading = false;
    })
    .addCase(fetchClientListWithLienholders.fulfilled, (state, action) => {
      state.clientListData = action.payload;
      state.isClientListLoading = false;
    })

    // Client List Details ----------
    // lender
    .addCase(fetchLenderDetails.pending, (state) => {
      state.lenderDetailsLoading = true;
    })
    .addCase(fetchLenderDetails.rejected, (state) => {
      state.lenderDetailsLoading = false;
    })
    .addCase(fetchLenderDetails.fulfilled, (state, action) => {
      state.lenderDetailsData = action.payload;
      state.lenderDetailsLoading = false;
    })
    // lienholder
    .addCase(fetchLienholderDetails.pending, (state) => {
      state.lienholderDetailsLoading = true;
    })
    .addCase(fetchLienholderDetails.rejected, (state) => {
      state.lienholderDetailsLoading = false;
    })
    .addCase(fetchLienholderDetails.fulfilled, (state, action) => {
      state.lienholderDetailsData = action.payload;
      state.lienholderDetailsLoading = false;
    })
    // lienholder with lender
    .addCase(fetchLienholderWithLenderDetails.pending, (state) => {
      state.lienholderLenderDetailsLoading = true;
    })
    .addCase(fetchLienholderWithLenderDetails.rejected, (state) => {
      state.lienholderLenderDetailsLoading = false;
    })
    .addCase(fetchLienholderWithLenderDetails.fulfilled, (state, action) => {
      state.lienholderLenderDetailsData = action.payload;
      state.lienholderLenderDetailsLoading = false;
    })
    // TRR Lender
    .addCase(upsertTRRLender.pending, (state) => {
      state.targetRecoveryRateLoading = true;
    })
    .addCase(upsertTRRLender.rejected, (state) => {
      state.targetRecoveryRateLoading = false;
      state.isLenderEditTRRModalOpen = false;
    })
    .addCase(upsertTRRLender.fulfilled, (state) => {
      state.targetRecoveryRateLoading = false;
      state.isLenderEditTRRModalOpen = false;
    })
    // TRR Lienholder
    .addCase(upsertTRRLienholder.pending, (state) => {
      state.targetRecoveryRateLoading = true;
    })
    .addCase(upsertTRRLienholder.rejected, (state) => {
      state.targetRecoveryRateLoading = false;
      state.isLienholderEditTRRModalOpen = false;
    })
    .addCase(upsertTRRLienholder.fulfilled, (state) => {
      state.targetRecoveryRateLoading = false;
      state.isLienholderEditTRRModalOpen = false;
    })
    // Lender Edit TRR Modal
    .addCase(openEditClientGoalModal, (state) => {
      state.isLenderEditTRRModalOpen = true;
    })
    .addCase(closeEditClientGoalModal, (state) => {
      state.isLenderEditTRRModalOpen = false;
    })
    // Lienholder Edit TRR Modal
    .addCase(openEditClientGoalModalLienholder, (state) => {
      state.isLienholderEditTRRModalOpen = true;
    })
    .addCase(closeEditClientGoalModalLienholder, (state) => {
      state.isLienholderEditTRRModalOpen = false;
    })

    // -- default --
    .addDefaultCase((state) => state);
});
